import type { CaseDossier, CommerceQuote } from "../../data/workspace";

/** The quote the case agreed: the latest one the customer received, else the latest draft. */
export function agreedQuote(quotes: CommerceQuote[]): CommerceQuote | undefined {
  const sorted = [...quotes].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  return sorted.find((quote) => quote.status !== "draft") ?? sorted[0];
}

const text = (value: unknown) => (typeof value === "string" && value.trim() ? value.trim() : null);
/** A count the team recorded (0 included); null while nobody knows it yet. */
const recorded = (value: unknown) => (value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));
const positive = (value: unknown) => (Number(value) > 0 ? Number(value) : null);

/**
 * The case's date and group, from its one record: the booking when there is one, else the sale
 * (kept in step with quote and payment slip), else the quote.
 */
export function caseFacts(dossier: CaseDossier) {
  const booking = (dossier.bookings ?? [])[0];
  const opportunity = dossier.opportunity;
  const quote = agreedQuote(dossier.quotes ?? []);
  const school = (dossier.service ?? quote?.service) === "school";
  const date = text(booking?.booking_date)?.slice(0, 10) ?? text(opportunity?.requested_date)?.slice(0, 10) ?? quote?.date ?? null;
  const monthOnly = !booking && opportunity?.requested_date_precision === "month";
  return {
    school,
    quote,
    date,
    monthOnly,
    time: text(booking?.booking_time)?.slice(0, 5) ?? quote?.time?.slice(0, 5) ?? null,
    people: positive(booking?.people_count) ?? positive(opportunity?.people_count) ?? positive(quote?.people),
    teachers: recorded(opportunity?.teachers_count) ?? recorded(quote?.school?.teachers),
    companions: recorded(opportunity?.companions_count) ?? recorded(quote?.school?.companions),
  };
}

/** What the case still does not know, in words, to ask before recording a payment. */
export function missingCaseData(dossier: CaseDossier): string[] {
  const facts = caseFacts(dossier);
  const missing: string[] = [];
  if (!facts.date) missing.push("fecha");
  else if (facts.monthOnly) missing.push("día exacto");
  if (!facts.people) missing.push(facts.school ? "número de alumnos" : "número de personas");
  if (facts.school && facts.teachers === null) missing.push("profesores");
  if (facts.school && facts.companions === null) missing.push("acompañantes");
  return missing;
}
